/**
 * Shared auth-state CTAs for EVERY nav surface (top banner, hero nav, sticky
 * nav, mobile burger). One module owns the "signed in vs signed out" rendering
 * so all bars stay in lockstep and the contract is impossible to drift.
 *
 * The optimistic rule lives in {@link showDashboardCta} (pure + unit-tested):
 *   - first paint / hard refresh: Clerk reports `isLoaded: false`, so every bar
 *     paints the SIGNED-OUT CTA instantly. No spinner, no blank, no "Dashboard".
 *   - only once `useAppAuth()` has BOTH loaded AND confirmed `isSignedIn` do the
 *     bars swap to the signed-in CTA. `useAppAuth().isSignedIn` is Clerk's
 *     authoritative session signal (backed by the `__client_uat` cookie), so a
 *     lingering/stale `__session` JWT can never pin the nav to a logged-in state.
 *
 * "Dashboard" appears exactly ONCE per bar: the desktop/burger nav CTA owns the
 * word "Dashboard"; the top banner shows "Sign out" (never "Dashboard") when
 * signed in, so the two never duplicate it.
 */
import { useAppAuth } from '../lib/auth/app-auth'
import { localizedPath, useTranslation } from '../lib/i18n'
import { showDashboardCta } from '../lib/auth-cta'

/**
 * Optimistic sign-in hint shared by every CTA below. Defaults to `false`
 * (signed-out) until Clerk has loaded AND confirmed an active session.
 */
export function useSignedInHint(): boolean {
  const { isLoaded, isSignedIn } = useAppAuth()
  return showDashboardCta(isLoaded, isSignedIn)
}

/**
 * Top-banner utility action. Signed-out: the "Log in" link. Signed-in: a
 * "Sign out" button that terminates the Clerk session and returns home. Never
 * renders "Dashboard" - the nav CTA owns that word so it shows exactly once.
 */
export function TopBannerAuthAction() {
  const { t, locale } = useTranslation()
  const signedIn = useSignedInHint()
  const { signOut } = useAppAuth()

  if (signedIn) {
    return (
      <button
        type="button"
        className="top-banner-login"
        onClick={() => void signOut({ redirectUrl: localizedPath('home', locale) })}
      >
        {t('common.signOut')}
      </button>
    )
  }

  return (
    <a className="top-banner-login" href={localizedPath('login', locale)}>
      {t('common.login')}
    </a>
  )
}

/**
 * Primary nav CTA shared by the hero and sticky bars. Signed-out: the "sign up"
 * CTA. Signed-in: the single "Dashboard" CTA -> the progress workspace.
 */
export function NavPrimaryCta({
  location = 'nav_hero',
}: {
  /** Which bar renders it (analytics `cta_clicked.location`). */
  location?: 'nav_hero' | 'nav_sticky'
} = {}) {
  const { t, locale } = useTranslation()
  const signedIn = useSignedInHint()

  return (
    <a
      className="primary-button compact"
      href={localizedPath(signedIn ? 'progress' : 'register', locale)}
      data-cta={location}
      data-cta-kind={signedIn ? 'dashboard' : 'signup'}
    >
      {signedIn ? t('common.dashboardCta') : t('common.signUpCta')}
    </a>
  )
}

/**
 * Burger-menu footer CTA. Signed-out: the "log in" ghost link + "sign up"
 * primary CTA pair. Signed-in: a single "Dashboard" CTA plus a "Sign out"
 * action. Kept here (not in the lazy dialog chunk) so it shares the exact same
 * optimistic rule as the desktop bars.
 */
export function MobileAuthCta({ signedIn }: { signedIn: boolean }) {
  const { t, locale } = useTranslation()
  const { signOut } = useAppAuth()

  if (signedIn) {
    return (
      <>
        <button
          type="button"
          className="ghost-button"
          onClick={() =>
            void signOut({ redirectUrl: localizedPath('home', locale) })
          }
        >
          {t('common.signOut')}
        </button>
        <a
          className="primary-button compact"
          href={localizedPath('progress', locale)}
          data-cta="mobile_menu"
          data-cta-kind="dashboard"
        >
          {t('common.dashboardCta')}
        </a>
      </>
    )
  }

  return (
    <>
      <a
        className="ghost-button"
        href={localizedPath('login', locale)}
        data-cta="mobile_menu"
        data-cta-kind="login"
      >
        {t('common.login')}
      </a>
      <a
        className="primary-button compact"
        href={localizedPath('register', locale)}
        data-cta="mobile_menu"
        data-cta-kind="signup"
      >
        {t('common.signUpCta')}
      </a>
    </>
  )
}
